import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useDataRefresh, formatLastUpdated } from '@/hooks/useDataRefresh';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import RemarksThreadCell from '@/components/RemarksThreadCell';
import OutputLinksCell, { getOutputLinks } from '@/components/OutputLinksCell';


// Helper function to safely parse JSON response
const safeJsonParse = async (response) => {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text || 'Unknown error' };
  }
};

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Filter, 
  CalendarIcon,
  ArrowUpDown,
  Eye,
  Copy,
  X,
  ExternalLink,
  FileText,
  Trash2,
  ChevronDown,
  Clock,
  Loader2,
  CheckCircle2,
  PauseCircle,
  Link2,
  Plus,
  Pencil,
  RefreshCw,
  RotateCcw,
  AlertCircle,
  FolderOpen,
  User,
  Star
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Statuses are now fetched from the API (managed in Settings → Control Panel → Task Log Status)
// Hardcoded list kept as fallback only inside `taskStatuses.js`.
import { fetchTaskStatuses, getStatusMeta } from '@/lib/taskStatuses';
import { TaskReferencesEditor } from '@/components/TaskReferencesEditor';
import { AudioRefRow, AudioDetailsView, AudioBriefFields } from '@/components/AudioTaskFields';

// `getStatusConfig` is no longer used — use `getStatusMeta(statusList, value)` from taskStatuses helper.

// Safe date formatter — never throws on missing / invalid values
const safeFormat = (value, pattern) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, pattern);
  } catch {
    return '—';
  }
};

// Normalize a task's references into a canonical `{ type, value }` array.
// Accepts:
//   - references: [{ type, value }, ...]
//   - references: ["url", "url", ...]  (legacy)
//   - reference_links: ["url", ...]     (very old legacy)
const normalizeReferences = (task) => {
  const out = [];
  const isValidValue = (v) => typeof v === 'string' && v.trim().length > 0;

  if (Array.isArray(task?.references) && task.references.length > 0) {
    for (const r of task.references) {
      if (r && typeof r === 'object' && isValidValue(r.value)) {
        out.push({
          type: ['text', 'audio'].includes(r.type) ? r.type : 'link',
          value: r.value.trim(),
          ...(r.name ? { name: r.name } : {}),
        });
      } else if (isValidValue(r)) {
        // Raw string in a rich array — treat as link
        out.push({ type: 'link', value: r.trim() });
      }
    }
  }

  // If references didn't yield anything, fall back to legacy reference_links
  if (out.length === 0 && Array.isArray(task?.reference_links)) {
    for (const l of task.reference_links) {
      if (isValidValue(l)) out.push({ type: 'link', value: l.trim() });
    }
  }

  return out;
};

const normalizeUrl = (s) => {
  const v = (s || '').trim();
  if (!v) return '';
  return v.startsWith('http://') || v.startsWith('https://') ? v : `https://${v}`;
};


export default function TaskLogPage() {
  const { isAdmin, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [brands, setBrands] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [skus, setSkus] = useState([]);
  
  // Filters
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [taskTypeFilter, setTaskTypeFilter] = useState('all');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  // Deep-link support — the Dashboard stat cards route in with
  // `?status=pending|in_progress|hold|completed|defunct|open`. `open` is
  // synthetic ("Open workload") = pending + in_progress + hold; we don't
  // apply it as a specific status because Task Log already surfaces those
  // three by default. We just pop a toast so the user knows they landed
  // on the right list.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('status');
    if (!s) return;
    const known = ['pending', 'in_progress', 'hold', 'completed', 'defunct'];
    if (known.includes(s)) setStatusFilter(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const [kindFilter, setKindFilter] = useState('all');
  const [reworkOnly, setReworkOnly] = useState(false);
  // Submitter dropdown — on Task Log we default to the logged-in user
  // (shown with a star). "all" and every other email remain selectable.
  // `submitterInitialized` ensures we only auto-apply the default ONCE,
  // right after currentUser resolves, so we don't clobber a manual choice.
  const [submitterFilter, setSubmitterFilter] = useState('all');
  const [submitterInitialized, setSubmitterInitialized] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  
  // Sorting
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // Rework request modal state
  const [reworkTask, setReworkTask] = useState(null);
  const [reworkReason, setReworkReason] = useState('');
  const [isSubmittingRework, setIsSubmittingRework] = useState(false);

  const currentEmail = (currentUser?.email || '').toLowerCase();

  // Task Log ONLY: default the submitter filter to the logged-in user on
  // first render (once currentUser resolves). Runs a single time — after
  // that the dropdown respects whatever the user picks (including "all").
  useEffect(() => {
    if (submitterInitialized) return;
    if (!currentEmail) return;
    setSubmitterFilter(currentEmail);
    setSubmitterInitialized(true);
  }, [currentEmail, submitterInitialized]);

  // Turn "first.last@wellversed.in" into "First Last" for a friendlier
  // Submitted By cell — falls back to the raw email if the local-part is
  // just a single token.
  const nameFromEmail = (email) => {
    if (!email) return '—';
    const local = String(email).split('@')[0] || email;
    return local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ') || email;
  };

  const canRequestRework = useCallback((task) => {
    if (!task) return false;
    if (task.status !== 'completed') return false;
    return (task.submitter_email || '').toLowerCase() === currentEmail;
  }, [currentEmail]);

  const openRework = (task) => {
    setReworkTask(task);
    setReworkReason('');
  };

  const submitRework = async () => {
    if (!reworkTask) return;
    const reason = reworkReason.trim();
    if (reason.length < 5) {
      toast.error('Please share a brief reason (min 5 characters)');
      return;
    }
    setIsSubmittingRework(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/${reworkTask.id}/rework`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) {
        const err = await safeJsonParse(res);
        throw new Error(err.detail || 'Failed to raise rework request');
      }
      const updated = await res.json();
      setTasks(prev => prev.map(t => (t.id === updated.id ? { ...t, ...updated } : t)));
      toast.success(`Rework requested on ${reworkTask.task_id}`);
      setReworkTask(null);
      setReworkReason('');
    } catch (e) {
      toast.error(e.message || 'Failed to raise rework request');
    } finally {
      setIsSubmittingRework(false);
    }
  };

  const resolveRework = async (task) => {
    if (!task) return;
    try {
      const res = await fetch(`${API_URL}/api/tasks/${task.id}/rework/resolve`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await safeJsonParse(res);
        throw new Error(err.detail || 'Failed to resolve rework');
      }
      const updated = await res.json();
      setTasks(prev => prev.map(t => (t.id === updated.id ? { ...t, ...updated } : t)));
      toast.success(`Rework marked resolved on ${task.task_id}`);
    } catch (e) {
      toast.error(e.message || 'Failed to resolve rework');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, brandsRes, typesRes, assigneesRes, skusRes, platformsRes, statusesRes] = await Promise.all([
        fetch(`${API_URL}/api/tasks`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/brands`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/task-types`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/assignees`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/skus`, { credentials: 'include' }),
        fetch(`${API_URL}/api/platforms`, { credentials: 'include' }),
        fetchTaskStatuses(true),
      ]);
      
      if (tasksRes.ok) {
        const fresh = await tasksRes.json();
        setTasks(fresh);
        // Re-sync the currently open View modal with backend truth so it
        // never lags behind an edit — belt-and-suspenders alongside the
        // in-flow update in handleSaveEdit.
        setSelectedTask(prev => {
          if (!prev) return prev;
          const match = fresh.find(t => t.id === prev.id);
          return match ? { ...prev, ...match } : prev;
        });
      }
      if (brandsRes.ok) setBrands(await brandsRes.json());
      if (typesRes.ok) setTaskTypes(await typesRes.json());
      if (assigneesRes.ok) setAssignees(await assigneesRes.json());
      if (skusRes.ok) setSkus(await skusRes.json());
      if (platformsRes.ok) {
        const p = await platformsRes.json();
        const names = (Array.isArray(p) ? p.map(x => (typeof x === 'string' ? x : x?.name)).filter(Boolean) : []);
        names.sort((a, b) => a.localeCompare(b));
        setPlatforms(names);
      }
      if (statusesRes) setStatusOptions(statusesRes);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh on window focus and every 2 minutes
  const { refresh, lastUpdated } = useDataRefresh(fetchData, {
    refreshOnFocus: true,
    refreshInterval: 120000, // 2 minutes
    minRefreshGap: 10000 // 10 seconds minimum between refreshes
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter and sort tasks
  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(task =>
        task.task_id.toLowerCase().includes(searchLower) ||
        task.brand_name.toLowerCase().includes(searchLower) ||
        task.design_brief.toLowerCase().includes(searchLower) ||
        task.submitter_email.toLowerCase().includes(searchLower)
      );
    }

    // Brand filter
    if (brandFilter && brandFilter !== 'all') {
      result = result.filter(task => task.brand_name === brandFilter);
    }

    // Platform filter
    if (platformFilter && platformFilter !== 'all') {
      result = result.filter(task => task.platform === platformFilter);
    }

    // Task type filter
    if (taskTypeFilter && taskTypeFilter !== 'all') {
      result = result.filter(task => task.task_type === taskTypeFilter);
    }

    // Media type filter — 'static' and 'video' match task_type.media_type
    // (design tasks are tagged as one or the other), while 'content' matches
    // task_type.kind (content pieces like blog posts / social captions live
    // there instead of on media_type).
    //
    // Fallback: some legacy tasks were submitted with a task_type name that
    // no longer exactly matches a row in the task_types config (e.g. "Blog"
    // vs. "Blog Post (Long-form)"). When the lookup misses, we infer
    // content-ness from the task_type STRING using a keyword heuristic so
    // the filter never returns silently blank.
    const CONTENT_KEYWORDS = /(blog|script|caption|article|post|copy|email|newsletter|social)/i;
    if (mediaTypeFilter && mediaTypeFilter !== 'all') {
      result = result.filter(task => {
        const taskTypeData = taskTypes.find(t => t.name === task.task_type);
        if (mediaTypeFilter === 'content') {
          if (taskTypeData?.kind === 'content') return true;
          if (!taskTypeData && CONTENT_KEYWORDS.test(task.task_type || '')) return true;
          return false;
        }
        return taskTypeData?.media_type === mediaTypeFilter;
      });
    }

    // Status filter
    if (statusFilter && statusFilter !== 'all') {
      result = result.filter(task => task.status === statusFilter);
    }

    // Kind filter (design / content)
    if (kindFilter && kindFilter !== 'all') {
      result = result.filter(task => (task.task_kind || 'design') === kindFilter);
    }

    // Rework Requested filter
    if (reworkOnly) {
      result = result.filter(task => !!task.rework_requested);
    }

    // Submitter filter — pick anybody (including yourself).
    if (submitterFilter && submitterFilter !== 'all') {
      const wanted = submitterFilter.toLowerCase();
      result = result.filter(task => (task.submitter_email || '').toLowerCase() === wanted);
    }

    // Date range filter
    if (dateFrom) {
      result = result.filter(task => 
        new Date(task.required_by_date) >= dateFrom
      );
    }
    if (dateTo) {
      result = result.filter(task => 
        new Date(task.required_by_date) <= dateTo
      );
    }

    // Sorting
    result.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'created_at' || sortBy === 'required_by_date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [tasks, taskTypes, search, brandFilter, platformFilter, taskTypeFilter, mediaTypeFilter, statusFilter, kindFilter, reworkOnly, submitterFilter, currentEmail, dateFrom, dateTo, sortBy, sortOrder]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredTasks.length / pageSize);
  const paginatedTasks = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTasks.slice(start, start + pageSize);
  }, [filteredTasks, currentPage, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, brandFilter, platformFilter, taskTypeFilter, mediaTypeFilter, statusFilter, kindFilter, reworkOnly, submitterFilter, dateFrom, dateTo, pageSize]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleDeleteTask = async (taskId, taskIdDisplay) => {
    if (!window.confirm(`Are you sure you want to delete task ${taskIdDisplay}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to delete task');
      }

      setTasks(tasks.filter(t => t.id !== taskId));
      setSelectedTask(null);
      toast.success(`Task ${taskIdDisplay} deleted successfully`);
    } catch (error) {
      toast.error(error.message || 'Failed to delete task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to update status');
      }

      const updatedTask = await response.json();
      setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
      if (selectedTask?.id === taskId) {
        setSelectedTask(updatedTask);
      }
      toast.success(`Status updated to ${getStatusMeta(statusOptions, newStatus).label}`);
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const handleAssigneeChange = async (taskId, assigneeId) => {
    try {
      const assignee = assignees.find(a => a.id === assigneeId);
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/assignee`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          assignee_id: assigneeId || null, 
          assignee_name: assignee?.name || null 
        })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to update assignee');
      }

      const updatedTask = await response.json();
      setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
      if (selectedTask?.id === taskId) {
        setSelectedTask(updatedTask);
      }
      toast.success(assignee ? `Assigned to ${assignee.name}` : 'Unassigned');
    } catch (error) {
      toast.error(error.message || 'Failed to update assignee');
    }
  };

  const handleExpectedEndDateChange = async (taskId, date) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/details`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          expected_end_date: date ? date.toISOString() : null
        })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to update expected end date');
      }

      const updatedTask = await response.json();
      setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
      if (selectedTask?.id === taskId) {
        setSelectedTask(updatedTask);
      }
      toast.success('Expected end date updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update expected end date');
    }
  };

  const handleOutputUpdate = async (taskId, outputLinks) => {
    // outputLinks can be a string (legacy single) or array (multi)
    try {
      const body = Array.isArray(outputLinks)
        ? { output_links: outputLinks }
        : { output_link: (outputLinks || '').trim() };
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/output`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to update output');
      }

      const updatedTask = await response.json();
      setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
      if (selectedTask?.id === taskId) setSelectedTask(updatedTask);
      toast.success('Output updated');
      return updatedTask;
    } catch (error) {
      toast.error(error.message || 'Failed to update output');
    }
  };

  const handleRemarksUpdate = async (taskId, remarks) => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/details`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ remarks: remarks.trim() })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to update remarks');
      }

      const updatedTask = await response.json();
      setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
      toast.success('Remarks updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update remarks');
    }
  };

  // (Output link state moved into OutputLinksCell component)

  const clearFilters = () => {
    setSearch('');
    setBrandFilter('all');
    setPlatformFilter('all');
    setTaskTypeFilter('all');
    setStatusFilter('all');
    setKindFilter('all');
    setReworkOnly(false);
    setSubmitterFilter('all');
    setDateFrom(null);
    setDateTo(null);
  };

  // Edit Task handlers
  const openEditTask = (task) => {
    const meta = getStatusMeta(statusOptions, task.status);
    if (meta.is_terminal) {
      toast.error(`${meta.label} tasks cannot be edited`);
      return;
    }
    setEditingTask({
      ...task,
      required_by_date: task.required_by_date ? new Date(task.required_by_date) : null,
      product_creatives: task.product_creatives || {},
      references: normalizeReferences(task),
    });
  };

  // Copy a submitted task into a fresh submission form so the user can edit & re-submit.
  const handleCopyTask = (task) => {
    if (!task) return;
    try {
      sessionStorage.setItem('taskCopyPrefill', JSON.stringify({
        task_id: task.task_id,
        task_kind: task.task_kind || 'design',
        brand_name: task.brand_name,
        platform: task.platform,
        task_type: task.task_type,
        dimensions: task.dimensions,
        design_brief: task.design_brief,
        audio_details: task.audio_details,
        references: task.references,
        reference_links: task.reference_links,
        notification_emails: task.notification_emails,
        cc_emails: task.cc_emails,
        featured_products: task.featured_products,
        product_creatives: task.product_creatives,
        assignee_id: task.assignee_id,
        assignee_name: task.assignee_name,
        remarks: task.remarks,
        required_by_date: task.required_by_date,
        expected_end_date: task.expected_end_date,
      }));
      const kind = ['content', 'audio'].includes(task.task_kind) ? task.task_kind : 'design';
      navigate(`/submit/${kind}`);
    } catch (e) {
      toast.error('Could not copy task — please try again');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingTask) return;
    
    setIsSavingEdit(true);
    try {
      const response = await fetch(`${API_URL}/api/tasks/${editingTask.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          brand_name: editingTask.brand_name,
          brand_id: editingTask.brand_id,
          platform: editingTask.platform,
          task_type: editingTask.task_type,
          dimensions: editingTask.dimensions,
          design_brief: editingTask.design_brief,
          ...(editingTask.task_kind === 'audio' ? { audio_details: editingTask.audio_details || {} } : {}),
          references: (editingTask.references || [])
            .map(r => ({ type: r.type, value: r.type === 'link' ? normalizeUrl(r.value) : (r.value || '').trim(), ...(r.name ? { name: r.name } : {}) }))
            .filter(r => r.value),
          required_by_date: editingTask.required_by_date ? editingTask.required_by_date.toISOString() : null,
          notification_emails: editingTask.notification_emails,
          featured_products: editingTask.featured_products,
          product_creatives: editingTask.platform?.toLowerCase() === 'meta' ? editingTask.product_creatives : undefined
        })
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to update task');
      }

      const updatedTask = await response.json();
      // Merge (functional setState) — protects against stale-closure clobber
      // if an auto-refresh (`refreshOnFocus`) races with the PUT, and keeps
      // derived fields (remarks_thread, output_links, rework_*, completed_at,
      // first_in_progress_at) that aren't part of the PUT payload.
      setTasks(prev => prev.map(t => t.id === editingTask.id ? { ...t, ...updatedTask } : t));
      // Keep the View / details modal in sync — if it's currently showing
      // the task we just edited, refresh its state too so the Eye popover
      // never shows the pre-edit values.
      setSelectedTask(prev => (prev && prev.id === editingTask.id ? { ...prev, ...updatedTask } : prev));
      setEditingTask(null);
      toast.success('Task updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update task');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const hasActiveFilters = search || brandFilter !== 'all' || platformFilter !== 'all' || 
    taskTypeFilter !== 'all' || mediaTypeFilter !== 'all' || statusFilter !== 'all' || reworkOnly || submitterFilter !== 'all' || dateFrom || dateTo;

  // Unique submitters across the loaded tasks. The logged-in user is pinned
  // at the top of the dropdown (with a "me" tag); everyone else follows
  // alphabetically. Users who aren't currently a submitter of any task
  // still appear if it's the logged-in user, so they can pre-select "me"
  // even before their first task lands.
  const submitters = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const em = (t.submitter_email || '').trim();
      if (!em) continue;
      const key = em.toLowerCase();
      if (!map.has(key)) map.set(key, em);
    }
    const currentEm = (currentUser?.email || '').trim();
    if (currentEm && !map.has(currentEm.toLowerCase())) {
      map.set(currentEm.toLowerCase(), currentEm);
    }
    const list = Array.from(map.values()).sort((a, b) => a.localeCompare(b));
    // Move the logged-in user to the top of the list.
    if (currentEm) {
      const idx = list.findIndex(x => x.toLowerCase() === currentEm.toLowerCase());
      if (idx > 0) {
        const [me] = list.splice(idx, 1);
        list.unshift(me);
      }
    }
    return list;
  }, [tasks, currentUser]);

  // Rework metrics — used by the stat tile + filter chip
  const reworkRequestedCount = useMemo(() => tasks.filter(t => t.rework_requested).length, [tasks]);
  const reworkTotalCount = useMemo(
    () => tasks.reduce((sum, t) => sum + (t.rework_count || 0), 0),
    [tasks]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EB4AC]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="task-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Task Log</h1>
          <p className="text-slate-500 mt-1">View and filter all submitted design tasks</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-400">
              Updated {formatLastUpdated(lastUpdated)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            className="text-slate-600"
            data-testid="refresh-tasks-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Rework tile + quick filter chip — visible when any task has rework requested */}
      {reworkRequestedCount > 0 && (
        <div
          className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-4 p-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-50/60"
          data-testid="rework-stat-strip"
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow-md shadow-amber-500/25 flex-shrink-0">
              <RotateCcw className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold text-amber-900 tabular-nums" data-testid="rework-tile-count">
                  {reworkRequestedCount}
                </p>
                <p className="text-sm font-semibold text-amber-800">
                  {reworkRequestedCount === 1 ? 'task' : 'tasks'} flagged for rework
                </p>
                {reworkTotalCount > reworkRequestedCount && (
                  <span className="text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900">
                    {reworkTotalCount} cycles total
                  </span>
                )}
              </div>
              <p className="text-xs text-amber-700/80 mt-0.5">
                A high rework count is a leading indicator of first-time-right issues — open each task to read the reason.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setReworkOnly(prev => !prev)}
            className={cn(
              'inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full border text-sm font-semibold transition-all whitespace-nowrap',
              reworkOnly
                ? 'bg-amber-500 text-white border-amber-500 shadow-md shadow-amber-500/30 hover:bg-amber-600'
                : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50 hover:border-amber-400'
            )}
            data-testid="rework-filter-toggle"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {reworkOnly ? 'Showing rework only' : 'Show rework only'}
            {reworkOnly && <X className="h-3.5 w-3.5 ml-1 opacity-80" />}
          </button>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-slate-500" />
              <CardTitle className="text-lg">Filters</CardTitle>
            </div>
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-700"
                data-testid="clear-filters-btn"
              >
                <X className="h-4 w-4 mr-1" />
                Clear all
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by Task ID, Brand, Brief, or Email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 border-slate-200"
              data-testid="search-input"
            />
          </div>

          {/* Filter Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Select value={brandFilter} onValueChange={setBrandFilter}>
              <SelectTrigger className="h-11 border-slate-200" data-testid="brand-filter">
                <SelectValue placeholder="All Brands" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map((brand) => (
                  <SelectItem key={brand.id} value={brand.name}>
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="h-11 border-slate-200" data-testid="platform-filter">
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                {platforms.map((platform) => (
                  <SelectItem key={platform} value={platform}>
                    {platform}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={taskTypeFilter} onValueChange={setTaskTypeFilter}>
              <SelectTrigger className="h-11 border-slate-200" data-testid="task-type-filter">
                <SelectValue placeholder="All Task Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Task Types</SelectItem>
                {taskTypes.map((type) => (
                  <SelectItem key={type.id} value={type.name}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={mediaTypeFilter} onValueChange={setMediaTypeFilter}>
              <SelectTrigger className="h-11 border-slate-200" data-testid="media-type-filter">
                <SelectValue placeholder="All Media" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Media</SelectItem>
                <SelectItem value="static">Static</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="content">Content</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11 border-slate-200" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statusOptions.map((status) => {
                  const meta = getStatusMeta(statusOptions, status.value);
                  return (
                    <SelectItem key={status.value} value={status.value}>
                      <div className="flex items-center gap-2">
                        <meta.Icon className="h-4 w-4" />
                        {status.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="h-11 border-slate-200" data-testid="kind-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="design">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-bold bg-violet-100 text-violet-700">🎨</span>
                    Design
                  </div>
                </SelectItem>
                <SelectItem value="content">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700">✍</span>
                    Content
                  </div>
                </SelectItem>
                <SelectItem value="audio">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-bold bg-orange-100 text-orange-700">🎧</span>
                    Audio
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Date Range */}
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "flex-1 h-11 justify-start text-left font-normal border-slate-200",
                      !dateFrom && "text-slate-400"
                    )}
                    data-testid="date-from-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateFrom ? format(dateFrom, "MMM d") : "From"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={setDateFrom}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "flex-1 h-11 justify-start text-left font-normal border-slate-200",
                      !dateTo && "text-slate-400"
                    )}
                    data-testid="date-to-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateTo ? format(dateTo, "MMM d") : "To"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={setDateTo}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count & Page Size */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-slate-500">
            Showing <span className="font-medium text-slate-900">{paginatedTasks.length}</span> of <span className="font-medium text-slate-900">{filteredTasks.length}</span> tasks
          </p>
          <Select value={submitterFilter} onValueChange={setSubmitterFilter}>
            <SelectTrigger className="h-8 w-[220px] text-xs border-slate-200 bg-white" data-testid="submitter-filter">
              <div className="flex items-center gap-1.5 min-w-0">
                {submitterFilter === currentEmail && currentEmail ? (
                  <Star className="h-3 w-3 text-amber-400 fill-amber-400 shrink-0" />
                ) : (
                  <User className="h-3 w-3 text-slate-400 shrink-0" />
                )}
                <SelectValue placeholder="All submitters" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All submitters</SelectItem>
              {submitters.map((em) => {
                const isMe = em.toLowerCase() === currentEmail;
                return (
                  <SelectItem key={em} value={em}>
                    <div className="flex items-center gap-2">
                      {isMe && (
                        <Star className="h-3 w-3 text-amber-400 fill-amber-400 shrink-0" />
                      )}
                      <span>{nameFromEmail(em)}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Show:</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="15">15</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table — reference-style card: rounded, soft shadow, no vertical dividers */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_8px_rgba(15,23,42,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-white border-b border-slate-100 hover:bg-white">
                <TableHead
                  className="cursor-pointer hover:text-slate-700 whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Submitted
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Submitted By</TableHead>
                <TableHead
                  className="cursor-pointer hover:text-slate-700 whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4"
                  onClick={() => handleSort('task_id')}
                >
                  <div className="flex items-center gap-1">
                    Task ID
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:text-slate-700 whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4"
                  onClick={() => handleSort('brand_name')}
                >
                  <div className="flex items-center gap-1">
                    Brand
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Platform</TableHead>
                <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Task Type</TableHead>
                <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Media</TableHead>
                <TableHead 
                  className="cursor-pointer hover:text-slate-700 whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4"
                  onClick={() => handleSort('required_by_date')}
                >
                  <div className="flex items-center gap-1">
                    Required By
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:text-slate-700 whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4 border-l border-slate-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Assignee</TableHead>
                <TableHead 
                  className="cursor-pointer hover:text-slate-700 whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4"
                  onClick={() => handleSort('expected_end_date')}
                >
                  <div className="flex items-center gap-1">
                    End Date
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Output</TableHead>
                <TableHead className="whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Remarks</TableHead>
                <TableHead className="text-right whitespace-nowrap text-slate-400 font-semibold text-[11px] uppercase tracking-wider py-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-12">
                    <p className="text-slate-500">No tasks found</p>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTasks.map((task) => {
                  const statusConfig = getStatusMeta(statusOptions, task.status);
                  const StatusIcon = statusConfig.Icon;
                  return (
                    <TableRow 
                      key={task.id} 
                      className={cn(
                        "border-b border-slate-100 last:border-b-0 transition-colors",
                        task.rework_requested
                          ? "bg-amber-50/40 hover:bg-amber-50/60"
                          : "hover:bg-slate-50/60"
                      )}
                    >
                      <TableCell className="text-slate-500 text-xs whitespace-nowrap py-4">
                        {format(new Date(task.created_at), "MMM d, HH:mm")}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm py-4" data-testid={`submitted-by-cell-${task.task_id}`}>
                        <div className="flex items-center gap-2.5">
                          {/* Circle initial avatar — same aesthetic as the
                              transaction reference: soft tinted background,
                              bold two-letter initials. Tinted color is
                              derived per email so each teammate has a
                              recognisable colour across the app. */}
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                            style={(() => {
                              const email = (task.submitter_email || '').toLowerCase();
                              let h = 0;
                              for (let i = 0; i < email.length; i++) h = ((h << 5) - h + email.charCodeAt(i)) | 0;
                              const hue = Math.abs(h) % 360;
                              return { backgroundColor: `hsl(${hue} 80% 94%)`, color: `hsl(${hue} 55% 40%)` };
                            })()}
                            title={task.submitter_email}
                          >
                            {(() => {
                              const nm = nameFromEmail(task.submitter_email);
                              const p = nm.split(/\s+/).filter(Boolean);
                              return (p.length === 1 ? p[0].slice(0, 2) : p[0][0] + p[p.length - 1][0]).toUpperCase();
                            })()}
                          </div>
                          <div className="flex flex-col leading-tight min-w-0">
                            <span className="text-slate-900 font-medium">{nameFromEmail(task.submitter_email)}</span>
                            <span className="text-[10px] text-slate-400 truncate max-w-[160px]" title={task.submitter_email}>{task.submitter_email}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-[#2EB4AC] whitespace-nowrap py-4">
                        <div className="flex items-center gap-1.5">
                          <span
                            title={`${{ content: 'Content', audio: 'Audio' }[task.task_kind] || 'Design'} Task`}
                            className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold ${task.task_kind === 'content' ? 'bg-emerald-100 text-emerald-700' : task.task_kind === 'audio' ? 'bg-orange-100 text-orange-700' : 'bg-violet-100 text-violet-700'}`}
                            data-testid={`task-kind-pill-${task.task_id}`}
                          >
                            {{ content: '✍', audio: '🎧' }[task.task_kind] || '🎨'}
                          </span>
                          {task.task_id}
                          {task.rework_requested && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 text-[10px] font-bold uppercase tracking-wider cursor-pointer hover:bg-amber-200 hover:border-amber-300 transition-colors"
                                  data-testid={`rework-badge-${task.task_id}`}
                                  title="Rework details"
                                >
                                  <RotateCcw className="h-2.5 w-2.5" />
                                  Rework{task.rework_count > 1 ? ` ×${task.rework_count}` : ''}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                side="top"
                                align="start"
                                className="w-[340px] p-4"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                      <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                                    </div>
                                    <p className="text-[11px] font-bold text-amber-700 uppercase tracking-[0.14em]">
                                      Rework Requested
                                    </p>
                                    {task.rework_count > 1 && (
                                      <span className="ml-auto text-[10px] font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                                        ×{task.rework_count}
                                      </span>
                                    )}
                                  </div>

                                  <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
                                    <p className="text-sm text-slate-800 whitespace-pre-wrap break-words leading-relaxed">
                                      {task.rework_reason || '(no reason provided)'}
                                    </p>
                                  </div>

                                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                    <span className="font-semibold text-slate-600">{task.rework_requested_by || 'unknown'}</span>
                                    {task.rework_requested_at && (
                                      <>
                                        <span className="text-slate-300">·</span>
                                        <span>{format(new Date(task.rework_requested_at), 'MMM d, HH:mm')}</span>
                                      </>
                                    )}
                                  </div>

                                  <div className="pt-1">
                                    {(task.submitter_email || '').toLowerCase() === currentEmail ? (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); resolveRework(task); }}
                                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-500 text-white text-xs font-bold uppercase tracking-[0.14em] hover:bg-emerald-600 transition-colors shadow-sm shadow-emerald-500/20"
                                        data-testid={`resolve-rework-${task.task_id}`}
                                      >
                                        <CheckCircle2 className="h-4 w-4" />
                                        Mark Resolved
                                      </button>
                                    ) : (
                                      <p className="text-[11px] text-slate-400 italic text-center">
                                        Only {task.submitter_email} can mark this resolved.
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{task.brand_name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-slate-100 text-slate-700 text-xs">
                          {task.platform}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[120px] text-xs">
                        <span className="truncate" title={task.task_type}>{task.task_type}</span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {(() => {
                          const taskTypeData = taskTypes.find(t => t.name === task.task_type);
                          // Video wins first (design tasks tagged as video).
                          if (taskTypeData?.media_type === 'video') {
                            return <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">Video</Badge>;
                          }
                          // Content: matches by task_type.kind, OR — for legacy
                          // tasks whose task_type name doesn't have a config row
                          // — infer from keywords in the task_type string (same
                          // heuristic as the filter above).
                          const CONTENT_KEYWORDS = /(blog|script|caption|article|post|copy|email|newsletter|social)/i;
                          const isContent = taskTypeData?.kind === 'content'
                            || (!taskTypeData && CONTENT_KEYWORDS.test(task.task_type || ''));
                          if (isContent) {
                            return <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-xs">Content</Badge>;
                          }
                          return <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs">Static</Badge>;
                        })()}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {format(new Date(task.required_by_date), "MMM d")}
                      </TableCell>
                      {/* Design Team Section */}
                      <TableCell className="py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn("h-7 px-2 gap-1 border", statusConfig.pill)}
                              data-testid={`status-dropdown-${task.task_id}`}
                            >
                              <StatusIcon className={cn("h-3 w-3", task.status === 'in_progress' && "animate-spin")} />
                              <span className="text-xs">{statusConfig.label}</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            {statusOptions.map((option) => {
                              const meta = getStatusMeta(statusOptions, option.value);
                              return (
                                <DropdownMenuItem
                                  key={option.value}
                                  onClick={() => handleStatusChange(task.id, option.value)}
                                  className={cn(
                                    "cursor-pointer",
                                    task.status === option.value && "bg-slate-100"
                                  )}
                                >
                                  <meta.Icon className="h-4 w-4 mr-2" />
                                  {option.label}
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={task.assignee_id || 'unassigned'} 
                          onValueChange={(value) => handleAssigneeChange(task.id, value === 'unassigned' ? '' : value)}
                        >
                          <SelectTrigger className="h-7 w-[100px] text-xs border-dashed">
                            <SelectValue placeholder="Assign" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {assignees.map((assignee) => (
                              <SelectItem key={assignee.id} value={assignee.id}>
                                {assignee.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 text-xs px-2 hover:bg-slate-100">
                              {task.expected_end_date ? format(new Date(task.expected_end_date), "MMM d") : <span className="text-slate-400">Set</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={task.expected_end_date ? new Date(task.expected_end_date) : undefined}
                              onSelect={(date) => handleExpectedEndDateChange(task.id, date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                      <TableCell>
                        <OutputLinksCell task={task} onSave={handleOutputUpdate} />
                      </TableCell>
                      <TableCell className="py-4">
                        <RemarksThreadCell
                          task={task}
                          onThreadUpdate={(taskId, patch) => {
                            setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
                          }}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canRequestRework(task) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRework(task)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              data-testid={`rework-task-${task.task_id}`}
                              title={task.rework_requested ? 'Request another rework' : 'Request rework'}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                          {task.status !== 'completed' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditTask(task)}
                              className="text-slate-500 hover:text-blue-500"
                              data-testid={`edit-task-${task.task_id}`}
                              title="Edit Task"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedTask(task)}
                            className="text-slate-500 hover:text-[#2EB4AC]"
                            data-testid={`view-task-${task.task_id}`}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyTask(task)}
                            className="text-slate-500 hover:text-violet-600"
                            data-testid={`copy-task-${task.task_id}`}
                            title="Copy as new task"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTask(task.id, task.task_id)}
                              className="text-slate-500 hover:text-red-500"
                              data-testid={`delete-task-${task.task_id}`}
                              title="Delete Task"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <p className="text-sm text-slate-500">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      className={currentPage === pageNum ? "bg-[#2EB4AC] hover:bg-[#25908A]" : ""}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Task Detail Modal */}
      <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-[#2EB4AC]">{selectedTask?.task_id}</span>
              {selectedTask && (() => {
                const statusConfig = getStatusMeta(statusOptions, selectedTask.status);
                const StatusIcon = statusConfig.Icon;
                return (
                  <Badge className={cn("border", statusConfig.pill)}>
                    <StatusIcon className={cn("h-3 w-3 mr-1", selectedTask.status === 'in_progress' && "animate-spin")} />
                    {statusConfig.label}
                  </Badge>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-6 pt-4">
              {/* Status Change */}
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-500">Change Status:</span>
                <div className="flex gap-2 flex-wrap">
                  {statusOptions.map((option) => {
                    const isActive = selectedTask.status === option.value;
                    const meta = getStatusMeta(statusOptions, option.value);
                    return (
                      <Button
                        key={option.value}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleStatusChange(selectedTask.id, option.value)}
                        className={cn(
                          "h-8",
                          isActive ? "bg-[#2EB4AC] hover:bg-[#2EB4AC]/90" : ""
                        )}
                        data-testid={`modal-status-${option.value}`}
                      >
                        <meta.Icon className={cn("h-3.5 w-3.5 mr-1", option.value === 'in_progress' && isActive && "animate-spin")} />
                        {option.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Brand</p>
                  <p className="font-medium">{selectedTask.brand_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Platform</p>
                  <p className="font-medium">{selectedTask.platform}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Task Type</p>
                  <p className="font-medium">{selectedTask.task_type}</p>
                </div>
                {(selectedTask.task_kind || 'design') === 'design' && (
                  <div>
                    <p className="text-sm text-slate-500">Dimensions</p>
                    <p className="font-medium">{selectedTask.dimensions}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">Required By</p>
                  <p className="font-medium">
                    {safeFormat(selectedTask.required_by_date, "MMMM d, yyyy")}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Submitted At</p>
                  <p className="font-medium">
                    {safeFormat(selectedTask.created_at, "MMMM d, yyyy HH:mm")}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Assignee</p>
                  <Select 
                    value={selectedTask.assignee_id || 'unassigned'} 
                    onValueChange={(value) => handleAssigneeChange(selectedTask.id, value === 'unassigned' ? '' : value)}
                  >
                    <SelectTrigger className="h-8 w-full mt-1">
                      <SelectValue placeholder="Assign..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {assignees.map((assignee) => (
                        <SelectItem key={assignee.id} value={assignee.id}>
                          {assignee.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Expected End Date</p>
                  <p className="font-medium">
                    {safeFormat(selectedTask.expected_end_date, "MMMM d, yyyy")}
                  </p>
                </div>
              </div>

              {/* Remarks Section */}
              <div>
                <p className="text-sm text-slate-500 mb-2">Remarks</p>
                <div className="p-3 bg-slate-50 rounded-lg min-h-[60px]">
                  <p className="whitespace-pre-wrap text-sm">{selectedTask.remarks || <span className="text-slate-400">No remarks</span>}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">{`${{ content: 'Content', audio: 'Audio' }[selectedTask.task_kind] || 'Design'} Brief`}</p>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="whitespace-pre-wrap">{selectedTask.design_brief}</p>
                </div>
              </div>

              {selectedTask.task_kind === 'audio' && <AudioDetailsView details={selectedTask.audio_details} />}

              {(() => {
                const refs = normalizeReferences(selectedTask);
                if (refs.length === 0) return null;
                return (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">References ({refs.length})</p>
                    <div className="space-y-2">
                      {refs.map((ref, idx) => (
                        ref.type === 'audio' ? (
                          <AudioRefRow key={idx} name={ref.name} url={ref.value} testId={`task-view-audio-ref-${idx}`} />
                        ) : ref.type === 'text' ? (
                          <div key={idx} className="flex items-start gap-2 p-2.5 rounded-md bg-emerald-50/60 border border-emerald-100 text-sm text-slate-700">
                            <FileText className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                            <p className="whitespace-pre-wrap break-words min-w-0">{ref.value}</p>
                          </div>
                        ) : (
                          <a
                            key={idx}
                            href={ref.value.startsWith('http') ? ref.value : `https://${ref.value}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-[#2EB4AC] hover:underline break-all p-2.5 rounded-md bg-slate-50 border border-slate-100"
                          >
                            <ExternalLink className="h-4 w-4 shrink-0" />
                            <span>{ref.value}</span>
                          </a>
                        )
                      ))}
                    </div>
                  </div>
                );
              })()}

              {selectedTask.featured_products_details?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Featured Products</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.featured_products_details.map((product) => (
                      <Badge 
                        key={product.id} 
                        variant="secondary"
                        className="bg-[#2EB4AC]/10 text-[#2EB4AC] border border-[#2EB4AC]/20"
                      >
                        {product.product_name}
                        {product.creatives_count && (
                          <span className="ml-1.5 px-1.5 py-0.5 bg-[#2EB4AC] text-white text-[10px] rounded-full">
                            ×{product.creatives_count}
                          </span>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Output Link Section */}
              <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100/50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-[#2EB4AC]" />
                    <p className="font-medium text-slate-700">Output / Deliverable{getOutputLinks(selectedTask).length > 1 ? `s (${getOutputLinks(selectedTask).length})` : ''}</p>
                  </div>
                  <OutputLinksCell task={selectedTask} onSave={handleOutputUpdate} />
                </div>

                {getOutputLinks(selectedTask).length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No output link added yet — use the button above to add one or more deliverable links.</p>
                ) : (
                  <div className="space-y-1.5">
                    {getOutputLinks(selectedTask).map((url, idx) => (
                      <a
                        key={`${url}-${idx}`}
                        href={normalizeUrl(url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-[#2EB4AC] hover:underline break-all"
                      >
                        <ExternalLink className="h-4 w-4 flex-shrink-0" />
                        {url}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Submitter</p>
                  <p className="font-medium">{selectedTask.submitter_email}</p>
                </div>
                {selectedTask.notification_emails?.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500">Notification Recipients</p>
                    <p className="font-medium">
                      {selectedTask.notification_emails.join(', ')}
                    </p>
                  </div>
                )}
              </div>

              {/* Delete Button - Admin Only */}
              {isAdmin && (
                <div className="pt-4 border-t border-slate-200">
                  <Button
                    variant="outline"
                    onClick={() => handleDeleteTask(selectedTask.id, selectedTask.task_id)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="delete-task-modal-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Task
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Task Modal */}
      <Dialog open={!!editingTask} onOpenChange={() => setEditingTask(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-500" />
              Edit Task - {editingTask?.task_id}
            </DialogTitle>
          </DialogHeader>
          
          {editingTask && (
            <div className="space-y-4 py-4">
              {/* Brand */}
              <div>
                <Label className="text-slate-700 font-medium">Brand</Label>
                <Select 
                  value={editingTask.brand_id || ''} 
                  onValueChange={(value) => {
                    const brand = brands.find(b => b.id === value);
                    setEditingTask({
                      ...editingTask,
                      brand_id: value,
                      brand_name: brand?.name || ''
                    });
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map(brand => (
                      <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Platform */}
              <div>
                <Label className="text-slate-700 font-medium">Platform</Label>
                <Input 
                  value={editingTask.platform}
                  onChange={(e) => setEditingTask({...editingTask, platform: e.target.value})}
                  className="mt-1"
                />
              </div>

              {/* Task Type */}
              <div>
                <Label className="text-slate-700 font-medium">Task Type</Label>
                <Select 
                  value={editingTask.task_type} 
                  onValueChange={(value) => setEditingTask({...editingTask, task_type: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select task type" />
                  </SelectTrigger>
                  <SelectContent>
                    {taskTypes.map(type => (
                      <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(editingTask.task_kind || 'design') === 'design' && (
                <div>
                  <Label className="text-slate-700 font-medium">Dimensions</Label>
                  <Input 
                    value={editingTask.dimensions}
                    onChange={(e) => setEditingTask({...editingTask, dimensions: e.target.value})}
                    className="mt-1"
                  />
                </div>
              )}

              {/* Design Brief */}
              <div>
                <Label className="text-slate-700 font-medium">{`${{ content: 'Content', audio: 'Audio' }[editingTask.task_kind] || 'Design'} Brief`}</Label>
                <Textarea 
                  value={editingTask.design_brief}
                  onChange={(e) => setEditingTask({...editingTask, design_brief: e.target.value})}
                  className="mt-1 min-h-[100px]"
                />
              </div>

              <TaskReferencesEditor
                value={editingTask.references}
                allowText={editingTask.task_kind !== 'design'}
                onChange={(references) => setEditingTask({ ...editingTask, references })}
              />
              {editingTask.task_kind === 'audio' && (
                <AudioBriefFields
                  value={editingTask.audio_details || {}}
                  onChange={(audio_details) => setEditingTask({ ...editingTask, audio_details })}
                />
              )}

              {/* Required By Date */}
              <div>
                <Label className="text-slate-700 font-medium">Required By Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full mt-1 justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editingTask.required_by_date 
                        ? format(editingTask.required_by_date, "PPP")
                        : "Pick a date"
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={editingTask.required_by_date}
                      onSelect={(date) => setEditingTask({...editingTask, required_by_date: date})}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Featured Products */}
              <div>
                <Label className="text-slate-700 font-medium">Featured Products</Label>
                <p className="text-xs text-slate-500 mb-2">
                  {editingTask.featured_products?.length || 0} product(s) selected
                  {editingTask.platform?.toLowerCase() === 'meta' && editingTask.product_creatives && (
                    <span className="ml-2 text-[#2EB4AC]">
                      • {Object.values(editingTask.product_creatives).reduce((a, b) => a + b, 0)} total creatives
                    </span>
                  )}
                </p>
                
                {/* Selected Products with Meta Creative Count */}
                {editingTask.featured_products?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3 p-2 bg-slate-50 rounded-lg">
                    {editingTask.featured_products.map(productId => {
                      const sku = skus.find(s => s.id === productId);
                      if (!sku) return null;
                      const isMetaPlatform = editingTask.platform?.toLowerCase() === 'meta';
                      return (
                        <div 
                          key={productId}
                          className={`flex items-center gap-2 bg-white border border-slate-200 rounded-lg ${isMetaPlatform ? 'px-3 py-2' : 'pl-3 pr-1 py-1'}`}
                        >
                          <span className="text-sm text-slate-700">{sku.product_name}</span>
                          {isMetaPlatform && (
                            <div className="flex items-center gap-1 border-l border-slate-200 pl-2">
                              <span className="text-xs text-slate-500">Creatives:</span>
                              <Input
                                type="number"
                                min="1"
                                value={(editingTask.product_creatives || {})[productId] || 1}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 1;
                                  setEditingTask({
                                    ...editingTask,
                                    product_creatives: {
                                      ...(editingTask.product_creatives || {}),
                                      [productId]: value
                                    }
                                  });
                                }}
                                className="w-14 h-7 text-center text-sm p-1"
                              />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const newProducts = editingTask.featured_products.filter(id => id !== productId);
                              const newCreatives = { ...(editingTask.product_creatives || {}) };
                              delete newCreatives[productId];
                              setEditingTask({
                                ...editingTask,
                                featured_products: newProducts,
                                product_creatives: newCreatives
                              });
                            }}
                            className="p-1 hover:bg-slate-100 rounded"
                          >
                            <X className="h-3 w-3 text-slate-400 hover:text-red-500" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Search Products */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search products..."
                    value={editingTask.productSearch || ''}
                    onChange={(e) => setEditingTask({...editingTask, productSearch: e.target.value})}
                    className="pl-9 h-9"
                  />
                </div>
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {skus
                    .filter(sku => !editingTask.brand_id || sku.brand_id === editingTask.brand_id)
                    .filter(sku => {
                      if (!editingTask.productSearch) return true;
                      const searchLower = editingTask.productSearch.toLowerCase();
                      return sku.product_name?.toLowerCase().includes(searchLower) ||
                             sku.brand_name?.toLowerCase().includes(searchLower);
                    })
                    .map(sku => {
                      const isSelected = editingTask.featured_products?.includes(sku.id);
                      const isMetaPlatform = editingTask.platform?.toLowerCase() === 'meta';
                      return (
                        <label
                          key={sku.id}
                          className={cn(
                            "flex items-center gap-3 p-2 cursor-pointer hover:bg-slate-50 border-b last:border-b-0",
                            isSelected && "bg-[#2EB4AC]/5"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const currentProducts = editingTask.featured_products || [];
                              const currentCreatives = editingTask.product_creatives || {};
                              if (e.target.checked) {
                                setEditingTask({
                                  ...editingTask,
                                  featured_products: [...currentProducts, sku.id],
                                  product_creatives: isMetaPlatform 
                                    ? { ...currentCreatives, [sku.id]: 1 }
                                    : currentCreatives
                                });
                              } else {
                                const newCreatives = { ...currentCreatives };
                                delete newCreatives[sku.id];
                                setEditingTask({
                                  ...editingTask,
                                  featured_products: currentProducts.filter(id => id !== sku.id),
                                  product_creatives: newCreatives
                                });
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-[#2EB4AC] focus:ring-[#2EB4AC]"
                          />
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {sku.image_url ? (
                              <img src={sku.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                                <span className="text-xs text-slate-400">No img</span>
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-700 truncate">{sku.product_name}</p>
                              <p className="text-xs text-slate-500">{sku.brand_name}</p>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  {skus.filter(sku => !editingTask.brand_id || sku.brand_id === editingTask.brand_id).length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">No products available</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTask(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={isSavingEdit}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSavingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rework Request Modal — only the submitter can raise */}
      <Dialog open={!!reworkTask} onOpenChange={(o) => { if (!o) { setReworkTask(null); setReworkReason(''); } }}>
        <DialogContent className="max-w-lg" data-testid="rework-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <RotateCcw className="h-5 w-5" />
              Request Rework
              {reworkTask?.task_id && (
                <span className="ml-1 text-sm font-normal text-slate-500">· {reworkTask.task_id}</span>
              )}
            </DialogTitle>
          </DialogHeader>

          {reworkTask && (
            <div className="space-y-4">
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">
                  Task summary
                </p>
                <p className="text-sm font-medium text-slate-900">{reworkTask.brand_name} · {reworkTask.task_type}</p>
                <p className="text-xs text-slate-600 mt-1 line-clamp-2">{reworkTask.design_brief}</p>
                {reworkTask.rework_count > 0 && (
                  <p className="text-[11px] text-amber-700 font-semibold mt-2">
                    This task has been reworked {reworkTask.rework_count} time{reworkTask.rework_count === 1 ? '' : 's'} already
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="rework-reason" className="text-sm font-semibold text-slate-800">
                  What needs to change? <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="rework-reason"
                  value={reworkReason}
                  onChange={(e) => setReworkReason(e.target.value)}
                  placeholder="Describe what should be reworked — e.g. wrong product positioning, copy tone, missing CTA, etc."
                  rows={5}
                  className="mt-1.5 resize-none"
                  data-testid="rework-reason-input"
                  maxLength={2000}
                />
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-[11px] text-slate-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    The task status will not change — it will only be flagged for rework.
                  </p>
                  <p className="text-[11px] text-slate-400 tabular-nums">{reworkReason.length}/2000</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { setReworkTask(null); setReworkReason(''); }}
              disabled={isSubmittingRework}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitRework}
              disabled={isSubmittingRework || reworkReason.trim().length < 5}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="rework-submit-btn"
            >
              {isSubmittingRework ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Raise Rework Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
